'use client';
import { CaptchaRequestMessage, isCaptchaValidationError } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from 'process';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { IoIosSend } from 'react-icons/io';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const generalLayout = useGlobalStore((s) => s.generalLayout);

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(true);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => {
      setIsSubscribed(true);
      trackFormSubmitSuccess({
        form_id: 'newsletter',
        form_name: 'newsletter_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: 2,
        send_to: generalLayout.marketing?.gtm_id,
      });
    },
    onError: (captchaRequestError) => { if (isCaptchaValidationError(captchaRequestError)) { setIsAlreadySubscribed(false); return; } setIsAlreadySubscribed(true); },
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  const isDesktop = useBreakpoint('sm');

  return (
    <section className={cn('custom-section', 'py-12 sm:py-14 lg:py-16')}>
      <div
        className={cn(
          'custom-container',
          'primary-gradient relative flex flex-col items-center justify-center gap-4 rounded-[8px] p-6 sm:gap-5 sm:p-8 md:p-10 lg:gap-6 lg:p-12',
        )}
      >
        <Heading title="Odoberať newsletter" color="white" />
        <form
          className={cn('w-full max-w-[700px]', 'ea-homepage-newsletter__form')}
          onSubmit={handleSubmit}
        >
<CaptchaRequestMessage error={subscribeMutation.error} />
          <Input
            aria-label={systemLabels('email')}
            id="newsletter-email"
            type="email"
            name="email"
            placeholder={t('text183')}
            onChange={(e) => setEmail(e.target.value)}
            className={{
              wrapper: cn(
                'h-16 rounded-[8px] border-none bg-white pr-1 text-muted-foreground',
                'ea-homepage-newsletter__form-item-email-input',
              ),
              main: cn('px-4 sm:px-6'),
            }}
            after={
              <Button
                type="submit"
                aria-label={t('text182')}
                size={isDesktop ? 'default' : 'none'}
                className={cn(
                  'h-14 w-14 sm:w-fit',
                  'ea-homepage-newsletter__form-item-email-submit-button',
                )}
                disabled={email.length === 0 || !checked || isSubscribed}
                isLoading={subscribeMutation.isPending}
              >
                <IoIosSend size={24} />
                {isDesktop && t('text182')}
              </Button>
            }
          />
        </form>
      </div>
    </section>
  );
}
